import { useEffect, useState } from "react";
import { Clock, DollarSign, Mail, FileText, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

const relTime = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const d = Math.floor(diff / 86400_000);
  if (d === 0) return "today";
  if (d === 1) return "1 day ago";
  if (d < 30) return `${d} days ago`;
  const m = Math.floor(d / 30);
  return m === 1 ? "1 month ago" : `${m} months ago`;
};

const ICONS = {
  levy: { icon: DollarSign, bg: "#fee2e2", c: "var(--alert)" },
  message: { icon: Mail, bg: "var(--blue-light)", c: "var(--blue-dark)" },
  minutes: { icon: FileText, bg: "#dcfce7", c: "#166534" },
  compliance: { icon: CheckCircle2, bg: "#dcfce7", c: "#166534" },
};

/** Owner activity feed — last ~5 events across levies + messages + minutes + compliance.
 *  Composed from existing endpoints. Sits BELOW the 3 hero cards on Owner Portal. */
export default function OwnerActivityFeed({ schemeId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!schemeId) return;
    let mounted = true;
    (async () => {
      try {
        const [levies, threads, minutes, compliance] = await Promise.all([
          api.get(`/schemes/${schemeId}/levies`).catch(() => ({ data: [] })),
          api.get(`/schemes/${schemeId}/messaging/threads`).catch(() => ({ data: [] })),
          api.get(`/schemes/${schemeId}/meetings/minutes/published`).catch(() => ({ data: [] })),
          api.get(`/schemes/${schemeId}/compliance`).catch(() => ({ data: [] })),
        ]);
        const es = [];
        for (const l of (levies.data || []).slice(0, 3)) {
          if (l.issued_at) es.push({ kind: "levy", at: l.issued_at, title: `Levy issued — ${l.period_label}`, body: `$${(l.total_amount || 0).toLocaleString()} · due ${l.due_date}` });
        }
        for (const t of (threads.data || []).slice(0, 3)) {
          es.push({ kind: "message", at: t.last_message_at || t.created_at, title: t.title || "New message", body: t.kind === "broadcast" ? "Committee broadcast" : "Direct thread" });
        }
        for (const m of (minutes.data || []).slice(0, 2)) {
          es.push({ kind: "minutes", at: m.published_at, title: "Minutes published", body: m.title || "" });
        }
        for (const c of (compliance.data || []).filter((x) => x.status === "completed").slice(0, 2)) {
          es.push({ kind: "compliance", at: c.completed_at, title: `Compliance completed — ${c.title}`, body: c.legislation_reference || "" });
        }
        es.sort((a, b) => (b.at || "").localeCompare(a.at || ""));
        if (mounted) { setEvents(es.slice(0, 5)); setLoading(false); }
      } catch (_e) { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [schemeId]);

  if (loading) return <div className="h-32 rounded-xl border animate-pulse bg-slate-50 mb-6" style={{ borderColor: "var(--border)" }} />;
  if (events.length === 0) return null;

  return (
    <div className="rounded-xl border bg-white p-5 mb-6" style={{ borderColor: "var(--border)" }} data-testid="owner-activity-feed">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-4">
        <Clock className="w-3.5 h-3.5" /> What's happening
      </div>
      <div className="space-y-3">
        {events.map((e, i) => {
          const meta = ICONS[e.kind];
          const Icon = meta.icon;
          return (
            <div key={i} className="flex items-start gap-3" data-testid={`owner-activity-item-${i}`}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: meta.bg }}>
                <Icon className="w-4 h-4" style={{ color: meta.c }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold" style={{ color: "var(--navy)" }}>{e.title}</div>
                {e.body && <div className="text-xs text-slate-500 mt-0.5 truncate">{e.body}</div>}
              </div>
              <div className="text-[11px] text-slate-500 shrink-0">{relTime(e.at)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
